// Libraries
import React from 'react';

// Supermove
import {Loading, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useResponsive, ResponsiveType, useUrlFilters, useModal, useDrawer} from '@supermove/hooks';
import {User} from '@supermove/models';

// App
import UserStatus, {UserStatusType} from '@shared/modules/User/enums/UserStatus';
import StaffUtils from 'modules/Organization/Settings/Staff/StaffUtils';
import CreateTeamDrawer from 'modules/Organization/Settings/Staff/components/CreateTeamDrawer';
import InviteMoverModal from 'modules/Organization/Settings/Staff/components/InviteMoverModal';
import OfficeMoversHeader from 'modules/Organization/Settings/Staff/components/OfficeMoversHeader';
import OfficeMoversSearch from 'modules/Organization/Settings/Staff/components/OfficeMoversSearch';
import OfficeMoversTable from 'modules/Organization/Settings/Staff/components/OfficeMoversTable';
import OfficeUserStatusTabs from 'modules/Organization/Settings/Staff/components/OfficeUserStatusTabs';

const PageContentContainer = Styled.View`
  flex: 1;
`;

const FiltersContainer = Styled.View<{responsive: ResponsiveType}>`
  padding-horizontal: ${({responsive}) => (responsive.desktop ? 0 : 16)}px;
`;

export const TEAMS = 'TEAMS' as const;
export type SelectedUserStatusType = UserStatusType | typeof TEAMS;

export type OfficeMoversContentV2FiltersType = {
  searchQuery: string | undefined;
  userStatus: SelectedUserStatusType;
};

const OrganizationSettingsStaffMoversContentV2 = () => {
  const responsive = useResponsive();
  const createTeamDrawer = useDrawer({name: 'Create Team Drawer'});
  const inviteMoverModal = useModal({
    name: 'Invite Mover Modal',
    enableTracking: true,
  });

  const urlFilters = useUrlFilters<OfficeMoversContentV2FiltersType>({
    getRoute: () => '/settings/staff/movers/crew-members',
    filterKeys: ['searchQuery', 'userStatus'],
    initialFilterValues: {userStatus: UserStatus.ACTIVE},
  });
  const selectedUserStatus = urlFilters.get('userStatus');

  const {loading, data, refetch} = useQuery(OrganizationSettingsStaffMoversContentV2.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      searchQuery: urlFilters.get('searchQuery') || '', // we get no results for an empty search query without the empty string fallback value
      statuses: [UserStatus.ACTIVE, UserStatus.INACTIVE],
    },
  });

  return (
    <Loading loading={loading}>
      {() => {
        const {viewer} = data;
        const {viewingOrganization: organization} = viewer;
        const {filteredMovers, crewTeams} = organization;
        const {activeUsers, pendingUsers, inactiveUsers} =
          User.getOfficeUsersByStatus(filteredMovers);
        const moverUsers = StaffUtils.getOfficeUsers({
          selectedUserStatus,
          activeUsers,
          pendingUsers,
          inactiveUsers,
        });

        return (
          <PageContentContainer>
            <OfficeMoversHeader
              viewerRole={viewer.role}
              urlFilters={urlFilters}
              createTeamDrawer={createTeamDrawer}
              inviteMoverModal={inviteMoverModal}
            />
            <Space height={responsive.desktop ? 24 : 16} />
            <FiltersContainer responsive={responsive}>
              <OfficeMoversSearch urlFilters={urlFilters} />
              <Space height={responsive.desktop ? 24 : 16} />
              <OfficeUserStatusTabs
                selectedUserStatus={selectedUserStatus}
                setSelectedUserStatus={(userStatus: SelectedUserStatusType) =>
                  urlFilters.handleUpdate({userStatus})
                }
                activeUsersCount={activeUsers.length}
                pendingUsersCount={pendingUsers.length}
                inactiveUsersCount={inactiveUsers.length}
                teamsCount={crewTeams.length}
                includePending={false}
              />
            </FiltersContainer>
            <OfficeMoversTable
              selectedUserStatus={selectedUserStatus}
              moverUsers={moverUsers}
              crewTeams={crewTeams}
              organization={organization}
              refetch={refetch}
              viewer={viewer}
            />
            <InviteMoverModal
              key={inviteMoverModal.key}
              isOpen={inviteMoverModal.isOpen}
              handleClose={inviteMoverModal.handleClose}
              refetch={refetch}
              viewer={data.viewer}
            />
            <CreateTeamDrawer
              isCrewTeam
              key={createTeamDrawer.key}
              isOpen={createTeamDrawer.isOpen}
              organization={organization}
              handleClose={() => {
                createTeamDrawer.handleClose();
                refetch();
              }}
            />
          </PageContentContainer>
        );
      }}
    </Loading>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OrganizationSettingsStaffMoversContentV2.query = gql`
  ${CreateTeamDrawer.fragment}
  ${User.getOfficeUsersByStatus.fragment}
  ${OfficeMoversTable.fragment}

  query OrganizationSettingsStaffMoversContentV2(
    $searchQuery: String,
    $statuses: [String],
  ) {
    ${gql.query}
    viewer {
      id
      role
      ...OfficeMoversTable_Viewer
      viewingOrganization {
        id
        filteredMovers(searchQuery: $searchQuery, statuses: $statuses) {
          id
          fullName
          ...OfficeMoversTable_User
          ...User_getOfficeUsersByStatus
        }
        officeTeams {
          id
          members {
            id
            role
          }
        }
        ...OfficeMoversTable_Organization
        ...CreateTeamDrawer
      }
    }
  }
`;

export default OrganizationSettingsStaffMoversContentV2;
